// Runtime config, resolved at BUILD time from Vite env vars (never hardcoded —
// see legacy-migration RULES.md and the task brief: "Base URL from runtime
// config ... never hard-coded"). `.env.example` documents every variable.
//
// `||`, not `??`: docker-compose passes every unset build arg as an EMPTY
// string (`${VAR:-}`), and the Dockerfile turns it into `ENV VAR=` — Vite then
// sees "" which `??` would keep. An empty value must mean "use the default".

/** Backend base URL — legacy `environment.apiUrl`
 * (`https://api-cryptopairscreener-com.appspot.com` in prod), now
 * `api.cryptopairscreener.com` per the FastAPI rewrite. */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

/** Google OAuth popup entry point — legacy `GET /user/login/google`
 * (audit §5). DEPRECATED 2026-09-26: the backend route now returns `410`
 * (see legacy-migration/RULES.md); kept only so nothing crashes if some
 * stray code still imports this. Use `AUTH0_LOGIN_URL` instead —
 * `Auth0LoginButton`'s `connection=google-oauth2` passthrough replaces the
 * direct-Google popup. */
export const GOOGLE_LOGIN_URL: string = import.meta.env.VITE_GOOGLE_LOGIN_URL || `${API_BASE_URL}/user/login/google`

/** Origin the Google-login popup's `postMessage` reply must come from — the
 * fix for audit §10 defect 1 (unchecked postMessage origin). Defaults to the
 * API's own origin, which is correct as long as the popup is served from the
 * backend itself (as it is in the legacy contract). DEPRECATED alongside
 * `GOOGLE_LOGIN_URL` above. */
export const GOOGLE_LOGIN_EXPECTED_ORIGIN: string =
  import.meta.env.VITE_GOOGLE_LOGIN_ORIGIN || new URL(API_BASE_URL).origin

/** Auth0 login popup entry point — `GET /user/login/auth0` (owner decision
 * 2026-09-26, legacy-migration/RULES.md). Passed to `Auth0LoginButton`, or
 * through `buildAuth0PopupUrl` for the `?signup=1`/`?connection=...`
 * variants (register page, "Continue with Google"). */
export const AUTH0_LOGIN_URL: string = import.meta.env.VITE_AUTH0_LOGIN_URL || `${API_BASE_URL}/user/login/auth0`

/** Origin the Auth0-login popup's `postMessage` reply must come from — same
 * origin-check fix as `GOOGLE_LOGIN_EXPECTED_ORIGIN` above, reused unchanged
 * by `Auth0LoginButton`. Defaults to the API's own origin (the backend's
 * `AUTH0_FRONTEND_ORIGIN` setting must match this app's own origin, not the
 * API's — but the popup response comes FROM the API, which is what this
 * constant checks). */
export const AUTH0_LOGIN_EXPECTED_ORIGIN: string =
  import.meta.env.VITE_AUTH0_LOGIN_ORIGIN || new URL(API_BASE_URL).origin

/** Direct external microservice call kept for round-1 behaviour parity
 * (legacy `GrabbitbotCandleService`, audit §4 "External microservice call
 * (bypasses ApiClientService entirely)"). Redirecting this through the fleet
 * candle service's auth/gateway path is an explicit ROUND-2 item
 * (legacy-migration RULES.md, audit §11 point 7) — not in scope here. */
export const CANDLE_SERVICE_URL: string = import.meta.env.VITE_CANDLE_SERVICE_URL || 'https://candle.grabbitbot.com'

/** GCP-decoupling switch (owner goal 2026-10-09, runbook
 * legacy-migration/docs/golive/screener-gcp-decoupling.md): where the coin
 * sheet's ad hoc indicator overlay is computed. `api` (default since 0.8.0) is
 * this app's own backend, `POST /indicators/{indicator}/calculate/from-screener/
 * normal` (same body in, same body out, computed locally with the legacy
 * TA-Lib formulas, behind the user's login). `candle` is the old direct
 * browser call to candle.grabbitbot.com above, kept only as the explicit
 * rollback (`VITE_INDICATOR_CALC_SOURCE=candle`). Anything but `candle` is `api`. */
export type IndicatorCalcSource = 'candle' | 'api'
export const INDICATOR_CALC_SOURCE: IndicatorCalcSource =
  import.meta.env.VITE_INDICATOR_CALC_SOURCE === 'candle' ? 'candle' : 'api'

/** App build version, injected by Vite from package.json at build time
 * (see vite.config.ts `define`). Used by the static /health payload and
 * shown in the footer for support purposes. */
export const APP_VERSION: string = __APP_VERSION__

/** Where a successful Auth0 popup login/registration sends the user next.
 * 2026-09-27 live-bug fix: this app has no "returnTo" concept yet (no page
 * captures the URL it redirected FROM before sending someone to /login), so
 * every entry point (login, register, and the four "this link is no longer
 * valid, sign in instead" notice pages) lands on the same default — the
 * screener's own start page, matching legacy password-setup's `navigate('/coins')`. */
export const POST_LOGIN_PATH = '/coins'

/** Role string `RequireAuth`'s `requireRole` checks for admin-only pages.
 * Legacy gated the admin menu purely client-side on
 * `user.username.endsWith('@webbandflow.co.uk')` (audit §10 defect 4) — a
 * non-security-boundary the rewrite intentionally does not reproduce. The
 * rewritten backend is expected to put a real `"admin"` role in
 * `GET /user/current`'s `roles` array; the backend remains the actual
 * authorization boundary either way. */
export const ADMIN_ROLE = 'admin'
